import React, { useState, useEffect } from 'react'
import { Card, Switch, Button, message } from 'antd'
import { PictureOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { PHOTO_URL } from '../utils/apiutils'
import { useAuth } from '../hooks/AuthContext'
import api from '../utils/api'

const Container = styled.div`
  max-width: 800px;
  margin: auto;
  padding: 20px 0;

  @media (max-width: 768px) {
    padding-bottom: 80px;
  }
`

const CategoryCard = styled(Card)`
  margin-bottom: 20px;
`

const ServiceItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`

const ServiceName = styled.div`
  display: flex;
  align-items: center;
`

const ServiceImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  margin-right: 10px;
  object-fit: cover;
  flex-shrink: 0;
`

const ServicePlaceholder = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  margin-right: 10px;
  background-color: #f5f5f5;
  color: #bfbfbf;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
`

/**
 * 服務縮圖，若沒有 image 或載入失敗就顯示乾淨的灰底 + 相機 icon placeholder，
 * 避免原本破圖 + alt 文字疊在一起的視覺。
 */
const ServiceThumbnail = ({ image, alt }) => {
  const [failed, setFailed] = useState(false)
  const hasImage = !!image && !failed
  if (!hasImage) {
    return (
      <ServicePlaceholder aria-label={alt || ''} role='img'>
        <PictureOutlined />
      </ServicePlaceholder>
    )
  }
  return (
    <ServiceImage
      src={`${PHOTO_URL}${image}`}
      alt={alt}
      onError={() => setFailed(true)}
    />
  )
}
const ButtonGroup = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;

  @media (max-width: 768px) {
    display: none;
  }
`
const StyledButton = styled(Button)`
  width: 100%;
  height: 50px;
  font-size: 16px;
`

const FixedButtonContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 16px;
  background: #fff;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
  z-index: 1000;

  @media (min-width: 769px) {
    display: none;
  }
`

const AssignedServices = ({ employeeId, businessId: businessIdProp }) => {
  const [assignedServices, setAssignedServices] = useState([])
  const [categories, setCategories] = useState({})
  const [modifiedServices, setModifiedServices] = useState([])
  const { user } = useAuth()
  // 優先使用 props 傳入的 businessId（來自 URL），fallback 到登入帳號的 businessId
  const businessId = businessIdProp || user.businessId

  useEffect(() => {
    const fetchAssignedServicesAndCategories = async () => {
      try {
        const [servicesResponse, categoriesResponse] = await Promise.all([
          api.get(
            `/api/employees/${businessId}/${employeeId}/assigned-services`
          ),
          api.get(`/api/business_B/${businessId}/categories`)
        ])

        setAssignedServices(servicesResponse.data.assignedServices)

        const categoriesData = categoriesResponse.data.reduce(
          (acc, category) => {
            acc[category._id] = category.name
            return acc
          },
          {} // 添加初始值
        )
        setCategories(categoriesData)
      } catch (error) {
        console.error('Error fetching assigned services or categories:', error)
      }
    }

    fetchAssignedServicesAndCategories()
  }, [employeeId, businessId])

  const handleServiceToggle = (serviceId, enabled) => {
    const updatedServices = assignedServices.map((service) =>
      service._id === serviceId ? { ...service, isDisabled: !enabled } : service
    )
    setAssignedServices(updatedServices)

    const updatedModifiedServices = [...modifiedServices]
    const modifiedIndex = updatedModifiedServices.findIndex(
      (service) => service.serviceId === serviceId
    )
    if (modifiedIndex === -1) {
      updatedModifiedServices.push({
        serviceId,
        isDisabled: !enabled
      })
    } else {
      updatedModifiedServices[modifiedIndex].isDisabled = !enabled
    }
    setModifiedServices(updatedModifiedServices)
  }

  const handleSaveChanges = async () => {
    try {
      await Promise.all(
        modifiedServices.map((service) =>
          api.put(
            `/api/employees/${businessId}/products/${
              service.serviceId
            }/stylists/${employeeId}/${
              service.isDisabled ? 'disable' : 'enable'
            }`
          )
        )
      )
      message.success('服務狀態已更新')
      setModifiedServices([])
    } catch (error) {
      console.error('Error saving service changes:', error)
      message.error('Failed to save service changes.')
    }
  }

  const groupedServices = assignedServices.reduce((acc, service) => {
    const category = categories[service.category] || '未分類' // 將 'Default' 改為 '未分類'
    if (!acc[category]) acc[category] = []
    acc[category].push(service)
    return acc
  }, {})

  return (
    <Container>
      {Object.keys(groupedServices).length > 0 ? (
        <>
          {Object.entries(groupedServices).map(([category, services]) => (
            <CategoryCard key={category} title={category}>
              {services.map((service) => (
                <ServiceItem key={service._id}>
                  <ServiceName>
                    <ServiceThumbnail
                      image={service.image}
                      alt={service.name}
                    />
                    <span>{service.name}</span>
                  </ServiceName>
                  <Switch
                    checked={!service.isDisabled}
                    onChange={(enabled) =>
                      handleServiceToggle(service._id, enabled)
                    }
                  />
                </ServiceItem>
              ))}
            </CategoryCard>
          ))}
          <ButtonGroup>
            <StyledButton
              type='primary'
              onClick={handleSaveChanges}
              disabled={modifiedServices.length === 0}
            >
              保存
            </StyledButton>
          </ButtonGroup>

          <FixedButtonContainer>
            <StyledButton
              type='primary'
              onClick={handleSaveChanges}
              disabled={modifiedServices.length === 0}
              block
            >
              保存
            </StyledButton>
          </FixedButtonContainer>
        </>
      ) : (
        <>
          <p>
            此員工尚未被指派任何服務。請至「服務」頁面，在服務的設計師欄位中加入此員工。
          </p>
        </>
      )}
    </Container>
  )
}

export default AssignedServices
